import { computedFrom } from 'aurelia-framework';
import { boundMethod } from 'autobind-decorator';

/**
 * @typedef {Object} ProductFeedsContainerRouteParams
 * @property {?Number} accountId The ID of the selected account.
 * @property {?String} option    The name of the selected option.
 */

/**
 * This is the main container for the product feeds section. It renders the sidebar navigation
 * and the content.
 */
class ProductFeedsContainer {
  /**
   * Whether or not the container is loading information.
   * @type {Boolean}
   */
  loading = false;
  /**
   * The information of the selected account, if there's one.
   * @type {?AccountInformation}
   */
  selectedAccount = null;
  /**
   * The information of the selected option, if there's one.
   * @type {Object}
   */
  selectedOption = null;
  selectedSubOption = null;
  selectedAction = null;
  selectedProduct = null;
  selectedJob = null;
  cancelledJob = null;
  finishedJob = null;
  showNewJobModal = false;
  updateJobsList = false;
  subOptions = [
    {
      id: 'settings',
      title: 'Settings',
    },
    {
      id: 'categories',
      title: 'Categories',
    },
    {
      id: 'ids',
      title: 'IDs',
    },
    {
      id: 'extras',
      title: 'Extras',
    },
    {
      id: 'relations',
      title: 'Relations',
    },
  ];
  actionsList = [
    {
      id: 'activities',
      name: 'activities',
      title: 'Activities',
    },
  ];
  /**
   * The value for stopping or not the events in the CanDeactivate method.
   * @type {Boolean}
   */
  _preventDeactivation = false;
  /**
   * The subscription for the beforeunload event.
   * @type {Subscription}
   */
  _unloadSubscription = null;
  /**
   * @param {AppErrorPublisher}      appErrorPublisher      To publish possible errors when
   *                                                        loading accounts information.
   * @param {errorParser}            errorParser            Used for error parsing.
   * @param {Router}                 router                 To make redirections when there's no
   *                                                        option selected.
   * @param {SelectedAccountOptions} selectedAccountOptions To validate the account selection.
   * @param {ViewMessages}           viewMessages           To create messages.
   */
  constructor(
    appErrorPublisher,
    appUtils,
    errorParser,
    router,
    selectedAccountOptions,
    selectedJobOptions,
    selectedProductOptions,
    viewMessages,
  ) {
    'inject';

    this._settingsOption = {
      id: 'settings',
      title: 'Settings',
    };

    this._productsOption = {
      id: 'products',
      title: 'Products',
    };

    this._jobsOption = {
      id: 'jobs',
      title: 'Jobs',
    };

    this.options = [
      this._settingsOption,
      this._productsOption,
      this._jobsOption,
    ];

    /**
     * The parser the container will use to format possible errors.
     * @type {ParserrorWrapper}
     * @access protected
     * @ignore
     */
    this._productFeedsErrorParser = errorParser.wrap();
    /**
     * A local reference for the `appErrorPublisher` service.
     * @type {AppErrorPublisher}
     * @access protected
     * @ignore
     */
    this._appErrorPublisher = appErrorPublisher;
    this._appUtils = appUtils;
    /**
     * A local reference for the `router` service.
     * @type {Router}
     * @access protected
     * @ignore
     */
    this._router = router;
    /**
     * A local reference for the `selectedAccountOptions` service.
     * @type {SelectedAccountOptions}
     * @access protected
     * @ignore
     */
    this._selectedAccountOptions = selectedAccountOptions;

    this._selectedJobOptions = selectedJobOptions;
    this._selectedProductOptions = selectedProductOptions;
    this._viewMessages = viewMessages;
  }
  /**
   * This is called when the user navigates to the component. This method will just
   * fire up the method that validates the parameters and load all the necessary
   * information for the container to work with.
   * @param {ProductFeedsContainerRouteParams} params The route parameters.
   * @param {Route}                            route  The current route information.
   */
  activate(params, route) {
    this._updateParams(route, params);
  }
  /**
   * This is called when the container is attached. Inside it will add the `beforeunload`
   * method to prompt the user about the unsaved changes.
   */
  attached() {
    this._unloadSubscription = this._appUtils.attachWindowEvents('beforeunload', this._unloadHandler);
  }
  /**
   * This is called when container is deactivated. Inside it will validate if you have
   * unsaved changes so it can trigger the confirmation message or not.
   * @return {Boolean}
   */
  canDeactivate() {
    let result = true;
    if (this._preventDeactivation && this.displayOptions.showOption.settings) {
      result = this._viewMessages.confirm(
        'unsavedChanges',
        'Unsaved Changes',
        'You have <strong>unsaved changes</strong>, What do you want to do?',
        {
          cancelText: 'Stay Here',
          confirmText: 'Discard Changes',
        },
      )
      .then((answer) => {
        if (answer) {
          this._preventDeactivation = false;
        }
        return answer;
      });
    }
    return result;
  }
  createNewJob(job) {
    this.updateJobsList = true;
    this.viewJob(job);

    setTimeout(() => {
      this.updateJobsList = false;
    }, 1);
  }
  /**
   * This is called when the container is detached. Inside it will remove the `beforeunload`
   * method to avoid side effects.
   */
  detached() {
    this._unloadSubscription.dispose();
  }
  goBackToJobs() {
    this._router.navigateToRoute(
      'productFeeds',
      {
        accountId: this.selectedAccount.id,
        option: this.selectedOption.id,
      },
      {
        replace: true,
      },
    );
  }
  /**
   * This is called when the editor wants to notify about new changes in it.
   * @param {Boolean} hasChanged The notification value.
   */
  notifyEditorStatus(hasChanged) {
    this._preventDeactivation = hasChanged;
  }
  toggleNewJobModal() {
    this.showNewJobModal = !this.showNewJobModal;
  }
  updateCancelledJob() {
    this.cancelledJob = this.selectedJob.id;
  }
  updateFinishedJob(status) {
    this.finishedJob = {
      status,
      id: this.selectedJob.id,
    };
  }
  viewJob(job) {
    this._selectedJobOptions.select(this.selectedAccount.id, job);
    this._router.navigateToRoute(
      'productFeeds',
      {
        accountId: this.selectedAccount.id,
        option: this.selectedOption.id,
        itemId: job.id,
      },
      {
        replace: true,
      },
    );
  }
  viewProduct(product) {
    this._selectedProductOptions.select(this.selectedAccount.id, product);
    this._router.navigateToRoute(
      'productFeeds',
      {
        accountId: this.selectedAccount.id,
        option: this.selectedOption.id,
        itemId: product.id,
      },
    );
  }
  @computedFrom(
    'loading',
    'selectedAccount',
    'selectedOption',
    'selectedProduct',
    'selectedJob',
    'selectedSubOption',
  )
  get displayOptions() {
    const has = this._getSelectionState();
    const result = this._getDisplayOptionsBase(has);

    if (has.job) {
      result.showJob.show = true;
      result.showJob.route = this._router.generate('productFeeds', {
        accountId: this.selectedAccount.id,
        option: this._jobsOption.id,
      });
    } else if (has.product && this.selectedSubOption) {
      this.subOptions.forEach((subOption) => {
        result.showProduct[subOption.id] = this.selectedSubOption.id === subOption.id;
      });
      result.showProduct.route = this._router.generate('productFeeds', {
        accountId: this.selectedAccount.id,
        option: this._productsOption.id,
      });
    } else if (!has.productOrJob && has.option) {
      this.options.forEach((option) => {
        result.showOption[option.id] = this.selectedOption.id === option.id;
      });
    } else if (!has.productOrJob && !has.option && has.action) {
      result.showAction[this.selectedAction.id] = true;
    }

    return result;
  }
  _getDisplayOptionsBase(has) {
    return {
      showPlaceholder: !this.loading && !has.account,
      showLoading: this.loading || (has.product && !this.selectedSubOption),
      showOption: {},
      showProduct: {},
      showAction: {},
      showJob: {},
    };
  }
  _getRouteTitle(title, routeTitle) {
    const titles = this._appUtils.ensureArray(title);
    const limit = 33;
    const limitWithoutDots = 30;
    const parts = [
      ...titles.map((item) => (
        item.length > limit ?
          `${item.substr(0, limitWithoutDots)}...` :
          item
      )),
      routeTitle,
    ];

    return parts.join(' | ');
  }
  _getSelectionState() {
    const account = !!this.selectedAccount;
    const job = !!this.selectedJob;
    const product = !!this.selectedProduct;
    const productOrJob = job || product;
    const option = !!this.selectedOption;
    const action = !!this.selectedAction;

    return {
      account,
      job,
      product,
      productOrJob,
      option,
      action,
    };
  }
  @boundMethod
  _unloadHandler(event) {
    if (this._preventDeactivation && this.displayOptions.showOption.settings) {
      event.preventDefault();
      event.returnValue = '';
    }
  }
  /**
   * It validates the route parameters in order to see if it's necessary to load
   * information for the API.
   * The reason the order of the parameters is not the same as on `activate` is so
   * we can make `params` optional and set a default value. This way, there's no need
   * to check if `params` was recevied, it's always there.
   * @param {Route}                            route       The current route information,
   *                                                       to update the route title with
   *                                                       an account name.
   * @param {ProductFeedsContainerRouteParams} [params={}] The current route recevied
   *                                                       parameters.
   * @return {Promise<undefined,Error>}
   * @access protected
   * @ignore
   */
  _updateParams(route, params = {}) {
    this.loading = true;
    let firstStep;

    if (params.accountId) {
      /**
       * If there's a selected account ID, cast it into a number and validate it.
       */
      const accountId = Number(params.accountId);
      if (Number.isNaN(accountId)) {
        // If the account is not a valid number, remove any possible selection.
        this._selectedAccountOptions.unselect();
        this._selectedProductOptions.unselect();
        this.selectedAccount = null;
        this.selectedOption = null;
        this.selectedProduct = null;
        this.selectedSubOption = null;
        this.selectedJob = null;
        this.selectedAction = null;
        firstStep = Promise.resolve();
      } else {
        // If it's a real number, call the service that validates it and gets the information.
        firstStep = this._selectedAccountOptions.validate(accountId);
      }
    } else {
      // If there wasn't a selected account ID, remove any possible selection.
      this._selectedAccountOptions.unselect();
      this.selectedAccount = null;
      this.selectedOption = null;
      this.selectedProduct = null;
      this.selectedSubOption = null;
      this.selectedJob = null;
      this.selectedAction = null;
      firstStep = Promise.resolve();
    }

    return firstStep
    .then((account) => {
      let nextStep;
      if (account) {
        // We should trigger a reference change to this.selectedAccount if the id changed
        if (!this.selectedAccount || this.selectedAccount.id !== account.id) {
          this.selectedAccount = account;
          route.navModel.setTitle(this._getRouteTitle(
            this.selectedAccount.name,
            route.title,
          ));
        }

        const option = this.options.find(({ id }) => id === params.option);
        if (option) {
          this.selectedOption = option;
          this.selectedAction = null;
          if (params.itemId && params.option === this._productsOption.id) {
            this._selectedJobOptions.unselect();
            this.selectedJob = null;
            nextStep = this._selectedProductOptions.validate(
              this.selectedAccount.id,
              params.itemId,
            );
          } else if (params.itemId && params.option === this._jobsOption.id) {
            this._selectedProductOptions.unselect();
            this.selectedProduct = null;
            nextStep = this._selectedJobOptions.validate(
              this.selectedAccount.id,
              params.itemId,
            );
          } else {
            this.selectedProduct = null;
            this.selectedJob = null;
            this._selectedProductOptions.unselect();
            this._selectedJobOptions.unselect();
          }
        } else {
          this.selectedOption = null;
          this.selectedProduct = null;
          this.selectedJob = null;
          this._selectedProductOptions.unselect();
          this._selectedJobOptions.unselect();
          const action = this.actionsList.find(({ name }) => name === params.option);
          if (action) {
            this.selectedAction = action;
          } else {
            nextStep = {
              fallbackRedirection: true,
            };
          }
        }
      }

      return nextStep;
    })
    .then((item) => {
      const redirection = {
        trigger: false,
        products: false,
        product: false,
      };
      if (item) {
        if (item.fallbackRedirection) {
          redirection.trigger = true;
        } else if (params.option === this._productsOption.id) {
          this.selectedProduct = item;
          route.navModel.setTitle(this._getRouteTitle(
            [this.selectedProduct.name, this.selectedAccount.name],
            route.title,
          ));

          const subOption = this.subOptions.find(({ id }) => id === params.subOption);
          if (subOption) {
            this.selectedSubOption = subOption;
          } else {
            redirection.trigger = true;
            redirection.product = true;
          }
        } else if (params.option === this._jobsOption.id) {
          this.selectedJob = item;
          route.navModel.setTitle(this._getRouteTitle(
            ['Job detail', this.selectedAccount.name],
            route.title,
          ));
        }
      }

      return redirection;
    })
    .catch((error) => {
      const parsedError = this._productFeedsErrorParser(
        error,
        'There was an error loading the customer information',
      );

      this._appErrorPublisher.notify(error, parsedError.message);
    })
    .then((redirection) => {
      if (redirection && redirection.trigger) {
        if (redirection.product) {
          this._router.navigateToRoute(
            'productFeeds',
            {
              accountId: this.selectedAccount.id,
              option: this._productsOption.id,
              itemId: this.selectedProduct.id,
              subOption: this.subOptions[0].id,
            },
            {
              replace: true,
            },
          );
        } else {
          const option = redirection.products ?
            this._productsOption :
            this._settingsOption;

          this._router.navigateToRoute(
            'productFeeds',
            {
              accountId: this.selectedAccount.id,
              option: option.id,
            },
            {
              replace: true,
            },
          );
        }
      } else {
        this.loading = false;
      }
    });
  }
}

export { ProductFeedsContainer };
